// FeatureCard.js
// -----------------------------------------------------------------------------
// Flexible card for "ways to get involved" style grids. Supports either a top
// image OR a leading icon, a title, body text, and an optional action (rendered
// at the bottom). Fully presentational.
//
// Props:
//   - title    : node     (required)        card heading
//   - body     : node     (default undef)   supporting text
//   - image    : string   (default undef)   top image URL (takes priority)
//   - imageAlt : string   (default '')      alt text for the image
//   - icon     : node     (default undef)   leading icon (used when no image)
//   - action   : node     (default undef)   bottom action slot (e.g. button)
//   - href     : string   (default undef)   makes the whole card a link (ext)
//   - to       : string   (default undef)   makes the whole card a route link
//   - align    : 'left'|'center' (default 'left')
//   - sx       : object   (default {})       extra Joy sx overrides
// -----------------------------------------------------------------------------

import * as React from 'react';
import PropTypes from 'prop-types';
import Card from '@mui/joy/Card';
import CardContent from '@mui/joy/CardContent';
import AspectRatio from '@mui/joy/AspectRatio';
import Box from '@mui/joy/Box';
import Typography from '@mui/joy/Typography';
import Link from '@mui/joy/Link';
import { Link as RouterLink } from 'react-router-dom';

const FeatureCard = ({
  title,
  body,
  image,
  imageAlt = '',
  icon,
  action,
  href,
  to,
  align = 'left',
  sx = {},
}) => {
  // Zero-trust: guard required prop and constrain enum.
  if (title === undefined || title === null || title === '') {
    return null;
  }
  const resolvedAlign = align === 'center' ? 'center' : 'left';
  const safeImage = typeof image === 'string' && image.length > 0 ? image : null;

  // Optional whole-card link decorator (Joy "overlay" pattern).
  let overlayLink = null;
  if (typeof to === 'string' && to.length > 0) {
    overlayLink = (
      <Link
        overlay
        component={RouterLink}
        to={to}
        sx={{ textDecoration: 'none', color: 'inherit' }}
      />
    );
  } else if (typeof href === 'string' && href.length > 0) {
    overlayLink = (
      <Link
        overlay
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        sx={{ textDecoration: 'none', color: 'inherit' }}
      />
    );
  }

  return (
    <Card
      variant="outlined"
      sx={{
        height: '100%',
        overflow: 'hidden',
        transition: 'transform 150ms ease, box-shadow 150ms ease',
        '&:hover': overlayLink
          ? { transform: 'translateY(-4px)', boxShadow: 'md' }
          : undefined,
        ...sx,
      }}
    >
      {safeImage ? (
        <AspectRatio ratio="16/10" sx={{ mx: -2, mt: -2, mb: 0.5 }}>
          <img src={safeImage} alt={imageAlt} loading="lazy" />
        </AspectRatio>
      ) : null}

      {!safeImage && icon ? (
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 56,
            height: 56,
            borderRadius: 'lg',
            bgcolor: 'primary.50',
            color: 'primary.600',
            fontSize: '1.75rem',
            alignSelf: resolvedAlign === 'center' ? 'center' : 'flex-start',
          }}
        >
          {icon}
        </Box>
      ) : null}

      <CardContent sx={{ textAlign: resolvedAlign }}>
        <Typography level="h4" sx={{ color: 'text.primary' }}>
          {title}
        </Typography>
        {body ? (
          <Typography level="body-md" sx={{ mt: 1 }}>
            {body}
          </Typography>
        ) : null}
        {/* mt: auto pins the action to the card bottom so actions line up
            across a row of cards with different body lengths. */}
        {action ? <Box sx={{ mt: 'auto', pt: 2 }}>{action}</Box> : null}
      </CardContent>

      {overlayLink}
    </Card>
  );
};

FeatureCard.propTypes = {
  title: PropTypes.node.isRequired,
  body: PropTypes.node,
  image: PropTypes.string,
  imageAlt: PropTypes.string,
  icon: PropTypes.node,
  action: PropTypes.node,
  href: PropTypes.string,
  to: PropTypes.string,
  align: PropTypes.oneOf(['left', 'center']),
  sx: PropTypes.object,
};

export default FeatureCard;
